import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(false);
      setBootstrapped(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async ({ email, password }) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async ({
    email,
    password,
    marketing_consent,
    preferred_language,
    date_of_birth,
    adult_acknowledged,
  }) => {
    // Phase 20 addition — `date_of_birth` and `adult_acknowledged` were
    // dropped by an earlier Phase-1-era destructure that predated the
    // Adult Verification schema. Both are REQUIRED on the backend and
    // both are collected in AuthCard; forward them verbatim.
    const body = {
      email,
      password,
      marketing_consent,
      date_of_birth,
      adult_acknowledged,
    };
    if (preferred_language) body.preferred_language = preferred_language;
    const { data } = await api.post("/auth/signup", body);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* stateless server, ignore */
    }
    clearToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, bootstrapped, login, signup, logout, refetch: fetchMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
